/**
 * Computes human-readable diagnostics for known cross-field DQ rules by parsing
 * quarantine `input_data` JSON (same values shown in the mapped grid).
 */

import type { DqxContractRuleMeta } from "@/services/dqxContractRulesLookup";

function normalizeFieldKey(s: string): string {
  return s.trim().toLowerCase().replace(/\s+/g, "_").replace(/-/g, "_");
}

/**
 * Parse a numeric cell value; returns null if missing or not a number.
 */
function parseAmount(raw: unknown): number | null {
  if (raw === null || raw === undefined) return null;
  if (typeof raw === "number" && Number.isFinite(raw)) return raw;
  const s = String(raw).trim().replace(/,/g, "");
  if (s === "") return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

/**
 * Read a field from a row object using loose key matching (case, spaces, hyphens).
 */
function getField(row: Record<string, unknown> | null, logical: string): unknown {
  if (!row) return undefined;
  if (Object.prototype.hasOwnProperty.call(row, logical)) return row[logical];
  const want = normalizeFieldKey(logical);
  for (const k of Object.keys(row)) {
    if (normalizeFieldKey(k) === want) return row[k];
  }
  return undefined;
}

function formatMoney(n: number): string {
  return n.toLocaleString("en-GB", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

// Two message shapes name the total column first: the auto-generated default
// ("X does not reconcile with component fields") and a contract author's own
// hand-written message (e.g. "X must equal Y - Z").
const TOTAL_COLUMN_MESSAGE_PATTERNS = [
  /^\s*([^:]+?)\s+does not reconcile with component fields/i,
  /^\s*([^:]+?)\s+must equal\b/i,
];

export function totalColumnFromMessage(msg: string | undefined): string | undefined {
  if (!msg || typeof msg !== "string") return undefined;
  for (const pattern of TOTAL_COLUMN_MESSAGE_PATTERNS) {
    const raw = msg.match(pattern)?.[1]?.trim();
    if (raw) return raw;
  }
  return undefined;
}

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

// A two-term "X must equal A - B" message is a subtraction, not a sum -- matched exactly
// against the component names rather than scanning for any hyphen, since a field's own
// name can contain one too (e.g. "Reserve - Indemnity").
function isTwoTermSubtractionMessage(
  message: string | undefined,
  componentColumns: string[]
): boolean {
  if (!message || componentColumns.length !== 2) return false;
  const [first, second] = componentColumns;
  if (!first || !second) return false;
  const pattern = new RegExp(
    `must equal\\s+${escapeRegExp(first)}\\s*-\\s*${escapeRegExp(second)}\\s*$`,
    "i"
  );
  return pattern.test(message.trim());
}

function summationDetail(
  input: Record<string, unknown> | null,
  totalColumn: string,
  componentColumns: string[],
  message?: string
): string {
  const total = parseAmount(getField(input, totalColumn));
  const isSubtraction = isTwoTermSubtractionMessage(message, componentColumns);

  let sum = 0;
  const parts: string[] = [];
  componentColumns.forEach((field, index) => {
    const v = parseAmount(getField(input, field));
    const n = v ?? 0;
    sum += isSubtraction && index > 0 ? -n : n;
    parts.push(`${field}: ${formatMoney(n)}`);
  });

  const totalLabel = total === null ? "(missing or invalid)" : formatMoney(total);
  const diff = total === null ? null : Math.abs(total - sum);
  const diffSeg = diff === null ? "" : ` | |Total − sum|: ${formatMoney(diff)} (tolerance ≤ 0.01)`;
  const sumLabel = isSubtraction ? "Difference" : "Sum of components";

  return `${totalColumn}: ${totalLabel} | ${sumLabel}: ${formatMoney(sum)}${diffSeg}. ${parts.join("; ")}.`;
}

export interface QuarantineRuleLike {
  name?: string;
  message?: string;
  columns?: string[];
}

/**
 * Returns an extra line of context for known cross-field rules, or undefined.
 */
export function buildCrossFieldDetail(
  rule: QuarantineRuleLike,
  inputData: Record<string, unknown> | null,
  meta?: DqxContractRuleMeta
): string | undefined {
  const isSummationRule = meta?.rule_type?.trim().toLowerCase() === "summation";
  if (!isSummationRule) return undefined;

  // This row's own columns, independent of the name-keyed `meta` lookup.
  const rowColumns = Array.isArray(rule.columns)
    ? rule.columns.map((c) => String(c).trim()).filter(Boolean)
    : [];

  // Prefer structural total_column/components; only trust them if they match this
  // row's own columns (guards against a rule-name collision in the meta lookup).
  if (meta?.total_column && meta.components?.length) {
    const normalizedRowColumns = new Set(rowColumns.map(normalizeFieldKey));
    const totalMatchesRow = normalizedRowColumns.has(normalizeFieldKey(meta.total_column));
    const componentsMatchRow = meta.components.every((c) =>
      normalizedRowColumns.has(normalizeFieldKey(c))
    );
    if (totalMatchesRow && componentsMatchRow) {
      return summationDetail(
        inputData,
        meta.total_column,
        meta.components,
        typeof rule.message === "string" ? rule.message : undefined
      );
    }
    return undefined;
  }

  // Legacy fallback: message-parsing + column-order heuristic.
  const candidates = [
    ...(meta?.affected_columns ?? []).map((c) => String(c).trim()),
    ...rowColumns,
  ].filter(Boolean);
  const uniqueColumns = Array.from(new Set(candidates));
  if (uniqueColumns.length < 2) return undefined;

  const totalFromMessage = totalColumnFromMessage(
    typeof rule.message === "string" ? rule.message : undefined
  );
  const totalColumn =
    (totalFromMessage &&
      uniqueColumns.find((c) => normalizeFieldKey(c) === normalizeFieldKey(totalFromMessage))) ||
    uniqueColumns[0];
  if (!totalColumn) return undefined;

  const componentColumns = uniqueColumns.filter((c) => c !== totalColumn);
  if (componentColumns.length === 0) {
    return undefined;
  }

  return summationDetail(
    inputData,
    totalColumn,
    componentColumns,
    typeof rule.message === "string" ? rule.message : undefined
  );
}
